// Écriture versionnée d'un paramètre de collectivité (H9-01) : chaque enregistrement crée une
// nouvelle version avec motif et date d'effet ; les versions précédentes restent intactes.
import { Bandeau, Bouton, DateChamp, Formulaire, ZoneTexte } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { requete } from '../../api/client';
import type { Parametre } from '../../api/types';
import { AffichageErreur } from '../../commun/EtatRequete';
import { dateHeure, versIso } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import commun from '../../commun/commun.module.css';
import { Squelette } from '@cc/ui';

export function useParametre(cle: string) {
  return useQuery({
    queryKey: ['parametres', cle],
    queryFn: ({ signal }) => requete<Parametre>(`/parametres/${cle}`, { signal }),
  });
}

export interface ProprietesFormulaireParametre<T> {
  cle: string;
  titre: string;
  /** Valeur courante du formulaire, envoyée telle quelle (validée par le serveur). */
  valeur: T;
  /** Rendu des champs ; reçoit la valeur en vigueur pour l'initialisation et les erreurs par champ. */
  children: (enVigueur: Parametre, erreur: (champ: string) => string | null) => ReactNode;
}

export function FormulaireParametre<T>({ cle, titre, valeur, children }: ProprietesFormulaireParametre<T>) {
  const { contexte } = useSession();
  const parametre = useParametre(cle);
  const [motif, setMotif] = useState('');
  const [dateEffet, setDateEffet] = useState('');
  const modifiable = peut(contexte, 'parametres', 'modifier');
  const ecriture = useAction({
    executer: () =>
      requete<{ version: number }>(`/parametres/${cle}`, {
        methode: 'PUT',
        corps: { valeur, motif, ...(dateEffet ? { date_effet: versIso(dateEffet) } : {}) },
      }),
    succes: (r) => `${titre} : version ${r.version} enregistrée.`,
    invalider: [['parametres', cle], ['audit']],
    apres: () => {
      setMotif('');
      setDateEffet('');
    },
  });
  const erreur = (champ: string) => ecriture.erreursChamps.find((e) => e.champ === `valeur.${champ}` || e.champ === champ)?.message ?? null;

  if (parametre.isPending) return <Squelette lignes={4} />;
  if (parametre.error) return <AffichageErreur erreur={parametre.error} onReessayer={() => void parametre.refetch()} fonction={titre} />;
  const p = parametre.data;
  return (
    <Formulaire
      libelle={titre}
      onSoumettre={() => ecriture.lancer(undefined)}
      erreurs={ecriture.erreursChamps.map((e) => ({ champ: `param-${cle}-${e.champ.replace(/^valeur\./, '').replace(/\./g, '-')}`, message: e.message }))}
      actions={
        modifiable ? (
          <Bouton type="submit" chargement={ecriture.enCours}>
            Enregistrer une nouvelle version
          </Bouton>
        ) : null
      }
    >
      <p className={commun.description}>
        {p.defini
          ? `Version ${p.version} en vigueur depuis le ${dateHeure(p.date_effet)}${p.motif ? `, motif : « ${p.motif} »` : ''}.`
          : 'Paramètre non renseigné : aucune version enregistrée.'}
        {p.version_programmee ? ` Une version ${p.version_programmee} est programmée.` : ''}
      </p>
      {!modifiable ? <Bandeau type="info">Votre rôle permet de consulter ce paramètre, pas de le modifier.</Bandeau> : null}
      {ecriture.messageErreur && ecriture.erreursChamps.length === 0 ? <Bandeau type="erreur">{ecriture.messageErreur}</Bandeau> : null}
      <fieldset disabled={!modifiable} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {children(p, erreur)}
        <ZoneTexte id={`param-${cle}-motif`} libelle="Motif de la modification" value={motif} onChange={(e) => setMotif(e.target.value)} erreur={erreur('motif')} required />
        <DateChamp id={`param-${cle}-date-effet`} libelle="Date d'effet" avecHeure value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} aide="Laisser vide pour une application immédiate" facultatif />
      </fieldset>
    </Formulaire>
  );
}
